/**
 * @fileoverview Connect administrator deep links to independently owned management workspaces. `People.js` owns staff administration; `AiControlCenter.js` owns AI settings/spend navigation; `Credentials.js` owns encrypted credential handoffs; audit, phone, and documentation screens own their own reads/actions. `kc-internal-frontend/src/App.js` owns route admission. Backend endpoints independently enforce administrator permission; changing a tab does not execute a management action.
 */

import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { AppTab, AppTabView } from '../shared/AppTabView';
import AiControlCenter from './AiControlCenter';
import People from './People';
import EmployeeAccess from './EmployeeAccess';
import PhoneAdmin from './PhoneAdmin';
import PhoneDashboard from './PhoneDashboard';
import Docs from './Docs';
import Credentials from './Credentials';
import UsageAuditDashboard from './UsageAuditDashboard';
import WorkComparison from './WorkComparison';
import { AppPageHeader } from '../shared';
import useUrlTabState from '../shared/useUrlTabState';
import './Admin.css';

const ADMIN_TAB_IDS = [
    'people',
    'employee-access',
    'work-comparison',
    'phone',
    'phone-dashboard',
    'ai',
    'usage-audit',
    'credentials',
    'docs',
];

const SETTINGS_TABS = new Set(['intake', 'notification-rules', 'case-attribute-types']);
const LEGACY_AI_TABS = {
    'openai-spend': { view: 'spend', provider: 'openai' },
    'anthropic-spend': { view: 'spend', provider: 'anthropic' },
    'document-ai': { view: 'features', feature: 'document-ai-edits', panel: 'access' },
};

const LEGACY_TAB_ALIASES = {
    'employee-lifecycle': 'people',
    'employees': 'people',
    'attorneys': 'people',
    'attorney-rates': 'people',
}



function Admin() {
    const location = useLocation();
    const { activeIndex: activeTab, onTabChange } = useUrlTabState(ADMIN_TAB_IDS, {
        aliases: LEGACY_TAB_ALIASES,
    });

    useEffect(() => {
        document.title = "Admin"
    }, [])

    const requestedTab = new URLSearchParams(location.search).get('tab');
    if (SETTINGS_TABS.has(requestedTab)) {
        return <Navigate to={`/settings?tab=${requestedTab}`} replace />;
    }
    if (LEGACY_AI_TABS[requestedTab]) {
        const next = new URLSearchParams(location.search);
        next.set('tab', 'ai');
        next.delete('feature');
        next.delete('panel');
        Object.entries(LEGACY_AI_TABS[requestedTab]).forEach(([key, value]) => next.set(key, value));
        return <Navigate to={{ pathname: location.pathname, search: `?${next}`, hash: location.hash }} replace />;
    }

    return (
        <div className="admin-route">
            <AppTabView
                activeIndex={activeTab}
                onTabChange={onTabChange}
                variant="route"
                header={
                    <AppPageHeader
                        embedded
                        eyebrow="Internal Operations"
                        title="Admin"
                        icon="pi pi-cog text-xl"
                    />
                }
            >
                <AppTab header="People">
                    <People embedded />
                </AppTab>
                <AppTab header="Employee access">
                    <EmployeeAccess />
                </AppTab>
                <AppTab header="Time & work">
                    <WorkComparison />
                </AppTab>
                <AppTab header="Phone">
                    <PhoneAdmin />
                </AppTab>
                <AppTab header="Phone Dashboard">
                    <PhoneDashboard />
                </AppTab>
                <AppTab header="AI">
                    <AiControlCenter />
                </AppTab>
                <AppTab header="Activity audit">
                    <UsageAuditDashboard embedded />
                </AppTab>
                <AppTab header="Credentials">
                    <Credentials />
                </AppTab>
                <AppTab header="Docs">
                    <Docs embedded />
                </AppTab>
            </AppTabView>
        </div>
    )
}

export default Admin
